// ─── Las reglas del código que escribe el agente ─────────────────────────────
// ADR-2026-09-24-animacion-por-codigo. Un clip `animation` puede tener, en vez de
// una plantilla del registro, un componente React escrito por el sub-agente del
// backend a pedido del usuario ("una placa con el 30 % de descuento que entre
// desde abajo"). Ese código lo escribe un modelo, así que se lo trata como código
// de un desconocido: corre sólo adentro de un iframe sandbox en el preview y
// adentro del Chrome de Lambda en el export, y ANTES de eso tiene que pasar por
// estas reglas.
//
// Este archivo es la ÚNICA fuente de verdad de las reglas, y es puro (strings y
// regex) a propósito: lo importan
//   • el sandbox del preview (`src/sandbox/animacion.tsx`), como segunda barrera
//     después del backend;
//   • el clip host del render (`remotion/src/clips/CodigoClip.tsx`);
//   • y el backend lo COPIA en `atlas-backend/src/services/estudio/animacion/reglas.ts`
//     (otro repo, no puede importarlo). `reglas.espejo.test.ts` falla si las dos
//     copias dejan de decir lo mismo.

/** Tope del código de una versión. Un componente razonable son 3 a 8 mil chars. */
export const MAX_CODIGO_CHARS = 20_000;

/** Cuántas versiones guarda un clip. Al pasar, se descarta la más vieja que no sea la actual. */
export const MAX_VERSIONES = 20;

/** Duración de una animación por código, en segundos. */
export const DURACION_MIN_SEG = 1;
export const DURACION_MAX_SEG = 60;

/** Cuántas fotos/logos puede recibir una animación por props. */
export const MAX_ASSETS = 6;

/** Qué cobra cada pedido al generador, en créditos. Volver a una versión es gratis. */
export const CREDITOS_ANIMACION = { crear: 5, modificar: 3 } as const;

/**
 * Lo ÚNICO de Remotion que el código puede usar. Se inyecta por nombre como
 * argumentos de la función compilada, así que un nombre que no esté acá es
 * `ReferenceError` y no un import silencioso de otra cosa.
 *
 * Fuera a propósito: `Audio`, `Video`, `OffthreadVideo` (el clip es visual; el
 * audio y el video van como clips propios de la timeline), `delayRender` (un
 * `delayRender` sin `continueRender` cuelga el render de Lambda hasta el timeout),
 * `staticFile` (no hay archivos estáticos) y `Composition`/`registerRoot` (el host
 * ya es la composición).
 */
export const API_PERMITIDA_REMOTION = [
  'AbsoluteFill',
  'Sequence',
  'Series',
  'Img',
  'useCurrentFrame',
  'useVideoConfig',
  'interpolate',
  'interpolateColors',
  'spring',
  'Easing',
  // `random(semilla)` es el azar determinístico de Remotion: el mismo valor en
  // cada frame, en el preview y en el MP4. Es lo que reemplaza a `Math.random`.
  'random',
] as const;

export type NombreDeApiRemotion = (typeof API_PERMITIDA_REMOTION)[number];

/**
 * Lo que el código NO puede tener, con el motivo. Es una lista negra por regex,
 * o sea una barrera de tela: lo que de verdad aísla es el sandbox (origen opaco,
 * CSP) y que el render corre en Lambda sin secretos. Esto existe para que el
 * modelo se corrija ANTES de llegar ahí y para que un accidente (un `fetch` a una
 * foto) no se descubra con un clip en negro.
 */
export const PROHIBIDOS: ReadonlyArray<{ patron: RegExp; motivo: string }> = [
  { patron: /^\s*import\s/m, motivo: 'no se importa nada: React y Remotion ya están en scope' },
  { patron: /\brequire\s*\(/, motivo: 'no hay require: React y Remotion ya están en scope' },
  { patron: /\bexport\s+(?!default\b)/, motivo: 'sólo `export default` del componente' },
  { patron: /\bfetch\s*\(/, motivo: 'sin red: las imágenes llegan por props.assets' },
  { patron: /\bXMLHttpRequest\b/, motivo: 'sin red' },
  { patron: /\bWebSocket\b/, motivo: 'sin red' },
  { patron: /\bnavigator\b/, motivo: 'sin acceso al navegador' },
  { patron: /\bwindow\s*[.[]/, motivo: 'sin acceso a window: todo sale de las props y de useCurrentFrame' },
  { patron: /\bdocument\s*[.[]/, motivo: 'sin acceso al DOM: se dibuja con JSX' },
  { patron: /\blocalStorage\b|\bsessionStorage\b|\bindexedDB\b/, motivo: 'sin storage' },
  { patron: /\beval\s*\(/, motivo: 'sin eval' },
  { patron: /\bnew\s+Function\b|\bFunction\s*\(/, motivo: 'sin Function()' },
  { patron: /\bsetTimeout\s*\(|\bsetInterval\s*\(|\brequestAnimationFrame\s*\(/, motivo: 'sin timers: el tiempo es useCurrentFrame()' },
  { patron: /\bMath\.random\s*\(/, motivo: 'sin azar: cada frame tiene que salir igual en el preview y en el MP4 (usá random() de Remotion con semilla, o valores fijos)' },
  { patron: /\bDate\b/, motivo: 'sin Date: el tiempo es useCurrentFrame()' },
  { patron: /\bdangerouslySetInnerHTML\b/, motivo: 'sin HTML crudo' },
  { patron: /\buseEffect\s*\(|\buseLayoutEffect\s*\(|\buseState\s*\(|\buseRef\s*\(/, motivo: 'sin efectos ni estado: el componente es una función pura del frame' },
  { patron: /\bdelayRender\b|\bcontinueRender\b/, motivo: 'sin delayRender: las imágenes se cargan con <Img>' },
  { patron: /\bstaticFile\s*\(/, motivo: 'sin staticFile: las imágenes llegan por props.assets' },
  { patron: /\bhttps?:\/\//i, motivo: 'sin URLs escritas en el código: las imágenes llegan por props.assets' },
  // Remotion 4.0.452 (preview y Lambda) acepta UNA función de easing: una lista por tramo
  // pasa la compilación y revienta al dibujar con "easing is not a function"
  // (ADR-2026-09-26-easing-por-tramo-no-existe).
  { patron: /\beasing\s*:\s*\[/, motivo: 'easing es UNA función (Easing.out(Easing.cubic)), no una lista por tramo: para curvas distintas, un interpolate por tramo' },
];

/**
 * Primera pasada sobre el código: tamaño, prohibidos y que exporte un default.
 * Devuelve el motivo del rechazo o `null` si pasa. No compila nada (eso es
 * `compilar.ts`, que necesita Babel).
 */
export function lintarCodigo(codigo: string): string | null {
  if (typeof codigo !== 'string' || !codigo.trim()) return 'el código está vacío';
  if (codigo.length > MAX_CODIGO_CHARS) {
    return `el código tiene ${codigo.length} caracteres y el tope es ${MAX_CODIGO_CHARS}`;
  }
  for (const { patron, motivo } of PROHIBIDOS) {
    const m = patron.exec(codigo);
    if (m) return `«${m[0].trim()}» no está permitido: ${motivo}`;
  }
  if (!/\bexport\s+default\b/.test(codigo)) {
    return 'falta `export default` del componente';
  }
  return null;
}

/** Acota una duración pedida al rango permitido (y la redondea a décimas). */
export function acotarDuracion(seg: number | undefined, porDefecto: number): number {
  const n = typeof seg === 'number' && Number.isFinite(seg) ? seg : porDefecto;
  return Math.round(Math.min(DURACION_MAX_SEG, Math.max(DURACION_MIN_SEG, n)) * 10) / 10;
}
